// Billing API client — wired to /api/billing/* per API_CONTRACT.md §6.
//
// All requests use `credentials: 'include'` so the HttpOnly session cookie
// goes through. Money is in EUR cents on the wire — components format for
// display.
//
// 503 `billing_not_configured` = STRIPE_SECRET_KEY missing on the server.
// We surface this as `BillingDisabledError` so the UI can show a friendly
// "billing temporarily unavailable" notice instead of throwing.

import type {
  BillingStatePublic,
  ContactPackRefillQuote,
  ContactPackRefillResult,
  ContactPackRefillStatus,
  ContactPackSize,
  ContactPacksResponse,
  CreditPackSize,
  CreditPurchase,
  Refund,
  SubscriptionDoc,
  SubscriptionEvent,
  SubscriptionTier,
} from '@cortex/schemas';
import type { CheckoutBaseline } from './checkoutReadiness';

const BILLING_BASE = '/api/billing';

// ─── Types specific to the public catalog (GET /api/billing/plans) ───────
export type PlansResponse = {
  subscriptions: Array<{
    tier: SubscriptionTier;
    name: string;
    creditsPerMonth: number;
    pricePerMonthEur: number;
    pricePerMonthCents: number;
    available: boolean;
  }>;
  packs: Array<{
    credits: CreditPackSize;
    priceEur: number;
    priceCents: number;
    available: boolean;
  }>;
  trialEnabled: boolean;
  trialDays: number;
  trialCredits: number;
  trialPlan: SubscriptionTier;
};

// CreditEvent ledger entry — per API_CONTRACT.md §6.2
export type CreditEvent = {
  id: string;
  userId: string;
  delta: number;
  reason: string;
  balanceAfter: number;
  timestamp: number;
  metadata?: Record<string, unknown>;
};

// ─── Errors ──────────────────────────────────────────────────────────────

export class BillingError extends Error {
  status: number;
  code: string | null;
  // LEA-505: some errors carry data the caller needs (e.g. `amount_changed`
  // brings the new amount). Kept verbatim; callers read only fields they know.
  body: Record<string, unknown>;

  constructor(status: number, body: Record<string, unknown>) {
    super(typeof body.error === 'string' ? body.error : `Billing request failed (${status})`);
    this.name = 'BillingError';
    this.status = status;
    this.code = typeof body.error === 'string' ? body.error : null;
    this.body = body;
  }
}

/** Thrown when the server is missing STRIPE_SECRET_KEY (503). */
export class BillingDisabledError extends BillingError {
  constructor() {
    super(503, { error: 'billing_not_configured' });
    this.name = 'BillingDisabledError';
  }
}

async function billingFetch<T = unknown>(
  path: string,
  options: RequestInit = {},
): Promise<T> {
  const res = await fetch(BILLING_BASE + path, {
    ...options,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...((options.headers as Record<string, string>) ?? {}),
    },
  });

  if (!res.ok) {
    if (res.status === 401 && typeof window !== 'undefined') {
      window.location.replace('/login');
      return new Promise(() => {});
    }
    let body: Record<string, unknown> = {};
    try {
      body = (await res.json()) as Record<string, unknown>;
    } catch {
      /* non-JSON */
    }
    if (res.status === 503 && body.error === 'billing_not_configured') {
      throw new BillingDisabledError();
    }
    throw new BillingError(res.status, body);
  }

  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

// ─── Transient-error auto-retry (LEA-333 undo-504) ────────────────────────
//
// A gateway/Stripe blip surfaces as 503 (the API now fails fast with a JSON 503
// — deadline breach or a Stripe connection/timeout error — instead of letting
// the LB emit a bare 504; see apps/api getStripe + withDeadline) or 504 (if the
// gateway still wins the race). A genuine Stripe API/validation error stays 502
// and is NOT retried. The mutations below (cancel, undo, pause, resume) are all
// idempotent server-side, so we retry ONCE after a short backoff before
// surfacing an error — a transient blip self-heals with no user-visible failure.
const TRANSIENT_BILLING_STATUSES = new Set([503, 504]);

export function isTransientBillingError(err: unknown): boolean {
  return err instanceof BillingError && TRANSIENT_BILLING_STATUSES.has(err.status);
}

async function retryOnceOnTransient<T>(fn: () => Promise<T>): Promise<T> {
  try {
    return await fn();
  } catch (err) {
    if (!isTransientBillingError(err)) throw err;
    await new Promise((resolve) => setTimeout(resolve, 1200));
    return fn();
  }
}

// ─── LEA-505 — one-click contact-pack refill ─────────────────────────────
//
// The card that uses these: features/billing/ContactPackRefillCard.tsx. The
// server decides everything (availability, amount, eligibility); the client
// only shows what it gets back and sends the customer's click.
//
// `confirmationId` is minted once per confirmation card and reused for every
// retry of that card — the server turns it into the purchase id and the Stripe
// idempotency keys, so a double click or a transient-retry can never charge
// twice. A NEW card (new pack, new amount, after a result) gets a new id.

export async function getContactPackRefill(): Promise<ContactPackRefillStatus> {
  return billingFetch<ContactPackRefillStatus>('/checkout/contact-pack/refill');
}

export async function quoteContactPackRefill(
  packSize: ContactPackSize,
): Promise<ContactPackRefillQuote> {
  return retryOnceOnTransient(() =>
    billingFetch<ContactPackRefillQuote>('/checkout/contact-pack/refill/quote', {
      method: 'POST',
      body: JSON.stringify({ packSize }),
    }),
  );
}

export async function chargeContactPackRefill(args: {
  packSize: ContactPackSize;
  confirmationId: string;
  expectedAmountCents: number;
}): Promise<ContactPackRefillResult> {
  // Safe to retry: same confirmationId → same purchase, never a second charge.
  return retryOnceOnTransient(() =>
    billingFetch<ContactPackRefillResult>('/checkout/contact-pack/refill/charge', {
      method: 'POST',
      body: JSON.stringify(args),
    }),
  );
}

/**
 * What became of this confirmation card, without starting a purchase. For when
 * the /charge response was lost (network error, 503/504, reload). If the click
 * never reached the server, the server SEALS this id and answers
 * `failed / not_received` — nothing was booked, and a late /charge with the same
 * id can no longer charge.
 */
export async function settleContactPackRefillAttempt(
  confirmationId: string,
): Promise<ContactPackRefillResult> {
  return retryOnceOnTransient(() =>
    billingFetch<ContactPackRefillResult>(
      `/checkout/contact-pack/refill/attempt/${encodeURIComponent(confirmationId)}`,
      { method: 'POST', body: JSON.stringify({}) },
    ),
  );
}

export async function completeContactPackRefill(
  purchaseId: string,
): Promise<ContactPackRefillResult> {
  return retryOnceOnTransient(() =>
    billingFetch<ContactPackRefillResult>(
      `/checkout/contact-pack/refill/${encodeURIComponent(purchaseId)}/complete`,
      { method: 'POST', body: JSON.stringify({}) },
    ),
  );
}

// ─── Public — pricing catalog (no auth) ──────────────────────────────────

export async function getPlans(): Promise<PlansResponse> {
  return billingFetch<PlansResponse>('/plans');
}

// ─── Authenticated state + checkout ──────────────────────────────────────

export async function getBillingState(): Promise<BillingStatePublic> {
  return billingFetch<BillingStatePublic>('/state');
}

// ─── Pre-checkout baseline (LEA-511) ─────────────────────────────────────
//
// /billing/success has to know when the webhook is DONE. It cannot tell from the
// balance alone, because the starting balance isn't a constant: a direct2 user
// already holds 50 wallet credits before they ever check out, so "credits > 0"
// is true before anything happened (see lib/checkoutReadiness.ts for the two
// incidents this caused). So we snapshot the state just before the redirect and
// compare against it on return.
//
// sessionStorage, not localStorage: this is per-tab, per-checkout, and must not
// outlive the browsing session — a stale baseline from last week would be worse
// than none at all.
const CHECKOUT_BASELINE_KEY = 'cortex_checkout_baseline';

/**
 * Snapshot the pre-checkout state. Every caller runs this CONCURRENTLY with the
 * checkout-session POST (`Promise.all`), so it adds no wall-clock delay to the
 * click that redirects to Stripe.
 *
 * Fail-open by design: if the read or the write fails (Safari private mode
 * throws on setItem), we clear the key and the return page falls back to the
 * old absolute-threshold behaviour. A checkout must never break because we
 * couldn't take a measurement.
 */
export async function captureCheckoutBaseline(): Promise<void> {
  try {
    const state = await getBillingState();
    const baseline: CheckoutBaseline = {
      creditsRemaining: state.creditsRemaining,
      cycleResetAt: state.cycleResetAt,
    };
    sessionStorage.setItem(CHECKOUT_BASELINE_KEY, JSON.stringify(baseline));
  } catch {
    clearCheckoutBaseline();
  }
}

/** Returns null when absent/unparseable — the caller then uses the legacy path. */
export function readCheckoutBaseline(): CheckoutBaseline | null {
  try {
    const raw = sessionStorage.getItem(CHECKOUT_BASELINE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<CheckoutBaseline>;
    if (typeof parsed.creditsRemaining !== 'number' || typeof parsed.cycleResetAt !== 'number') {
      return null;
    }
    return { creditsRemaining: parsed.creditsRemaining, cycleResetAt: parsed.cycleResetAt };
  } catch {
    return null;
  }
}

export function clearCheckoutBaseline(): void {
  try {
    sessionStorage.removeItem(CHECKOUT_BASELINE_KEY);
  } catch {
    // storage unavailable — nothing to clean up
  }
}

export async function startSubscriptionCheckout(
  tier: SubscriptionTier,
): Promise<{ url: string; sessionId: string }> {
  // LEA-465: retry once on a transient 503/504 (Stripe blip / API deadline breach)
  // so the checkout-session POST self-heals instead of forcing the user to re-click
  // the plan CTA. A Checkout Session is create-and-redirect — an abandoned session
  // self-expires (24h) with no charge, so the retry can never double-charge.
  // LEA-511: baseline runs in parallel — it must not delay the redirect.
  const [, session] = await Promise.all([
    captureCheckoutBaseline(),
    retryOnceOnTransient(() =>
      billingFetch<{ url: string; sessionId: string }>('/checkout/subscription', {
        method: 'POST',
        body: JSON.stringify({ tier }),
      }),
    ),
  ]);
  return session;
}

export async function startCreditsCheckout(
  packSize: CreditPackSize,
): Promise<{ url: string; sessionId: string }> {
  // LEA-465: same transient-retry as the subscription checkout above.
  const [, session] = await Promise.all([
    captureCheckoutBaseline(),
    retryOnceOnTransient(() =>
      billingFetch<{ url: string; sessionId: string }>('/checkout/credits', {
        method: 'POST',
        body: JSON.stringify({ packSize }),
      }),
    ),
  ]);
  return session;
}

// ─── LEA-518 — first contact-pack purchase (Stripe Checkout) ─────────────
//
// The one-click refill above needs a card saved by an earlier pack Checkout, so a
// reveal customer's FIRST pack goes through Checkout. The server decides prices
// and the first-purchase discount (GET /contact-packs) and decides again when the
// Checkout is created, so the client never computes an amount.
export async function getContactPacks(): Promise<ContactPacksResponse> {
  return billingFetch<ContactPacksResponse>('/contact-packs');
}

export async function startContactPackCheckout(
  packSize: ContactPackSize,
): Promise<{ url: string; sessionId: string; firstPurchase: boolean }> {
  // Same transient-retry and baseline as the credits checkout: the pack lands in
  // creditsRemaining, which the return page waits for.
  const [, session] = await Promise.all([
    captureCheckoutBaseline(),
    retryOnceOnTransient(() =>
      billingFetch<{ url: string; sessionId: string; firstPurchase: boolean }>(
        '/checkout/contact-pack',
        {
          method: 'POST',
          body: JSON.stringify({ packSize }),
        },
      ),
    ),
  ]);
  return session;
}

// LEA-379: start the 49 € Prism tripwire Checkout. prismRunId (from the funnel)
// is forwarded so the webhook can seed the account from that run after purchase.
export async function startPrismTripwireCheckout(
  prismRunId?: string,
): Promise<{ url: string; sessionId: string }> {
  const [, session] = await Promise.all([
    captureCheckoutBaseline(),
    billingFetch<{ url: string; sessionId: string }>('/checkout/prism-tripwire', {
      method: 'POST',
      body: JSON.stringify({ prismRunId }),
    }),
  ]);
  return session;
}

// Stufe 4.3 — Inline (Embedded) Checkout. Same tripwire session, but Stripe
// `ui_mode: 'embedded'`: the backend returns a `clientSecret` (+ publishable key)
// instead of a hosted-checkout URL, and we mount Stripe's Embedded Checkout
// INSIDE the funnel (no separate window). Everything downstream is unchanged —
// the embedded flow still fires `checkout.session.completed` and Stripe redirects
// to the same `return_url` (/billing/success) on completion, so the webhook grant
// + day-4 schedule + seed all reuse the existing money path.
//
// `mode: 'embedded'` on the same route keeps the contract additive; a backend that
// doesn't yet support it can 400/404 and the caller falls back to the redirect.
export type PrismEmbeddedInit = { clientSecret: string; publishableKey: string };
export async function startPrismTripwireEmbedded(
  prismRunId?: string,
): Promise<PrismEmbeddedInit> {
  const [, init] = await Promise.all([
    captureCheckoutBaseline(),
    billingFetch<PrismEmbeddedInit>('/checkout/prism-tripwire', {
      method: 'POST',
      body: JSON.stringify({ prismRunId, mode: 'embedded' }),
    }),
  ]);
  return init;
}

// Stufe 4 (LEA-379) — the CUSTOM Payment-Element tripwire. Unlike the embedded
// Checkout (which shows Stripe's own summary → the literal "€49 every 3 days" line),
// the backend mints a default_incomplete €49/3-day sub and returns its PaymentIntent
// client secret; the funnel renders OUR honest summary + the Payment Element inline
// and confirms it. automatic_tax parity with the hosted flow means we collect billing
// name + address (+ optional VAT-ID) here. A 409 `element_mode_off` (both flags not
// set) makes the caller fall back to the embedded/redirect Checkout.
export type PrismElementBilling = {
  name: string;
  address: {
    country: string;
    postal_code?: string;
    city?: string;
    line1?: string;
    line2?: string;
    state?: string;
  };
  vatId?: { type: string; value: string };
};
export type PrismElementInit = {
  clientSecret: string;
  publishableKey: string;
  subscriptionId: string;
  // LEA-429: real figures from the just-minted invoice (cents) so the summary shows the
  // amount actually charged. amount = charged incl. tax; subtotal = net; reverseCharge =
  // EU-B2B VAT-ID (0% German VAT).
  amount: number;
  currency: string;
  subtotal: number;
  tax: number;
  reverseCharge: boolean;
};
export async function startPrismTripwirePaymentElement(
  billing: PrismElementBilling,
  prismRunId?: string,
): Promise<PrismElementInit> {
  // The Payment Element's return_url is /billing/success too, so it needs the
  // same baseline as the redirect flows.
  const [, init] = await Promise.all([
    captureCheckoutBaseline(),
    billingFetch<PrismElementInit>('/checkout/prism-tripwire/payment-element', {
      method: 'POST',
      body: JSON.stringify({ ...billing, prismRunId }),
    }),
  ]);
  return init;
}

// Cheap runtime probe for element mode WITHOUT creating a sub: the route checks the
// flag BEFORE validating the body, so an empty POST returns 409 when the flag is dark
// and 400 (invalid_body) when it's live. Lets the funnel decide up front whether to
// show the custom Payment-Element form or fall back to embedded/redirect Checkout —
// no build-time VITE flag, flips per-env with the backend. Fail-safe: any other outcome
// → off (stay on the proven Checkout path).
// 15.10.2026: off carries the reason in the same coarse form as the mint failures
// (`init_http_<status>` / `init_network`). Without it a funnel that never showed the form
// looked exactly like one where nobody typed.
export type PrismElementProbe = { on: true } | { on: false; code: string };
export async function probePrismElementMode(): Promise<PrismElementProbe> {
  try {
    await billingFetch('/checkout/prism-tripwire/payment-element', {
      method: 'POST',
      body: JSON.stringify({}),
    });
    return { on: true }; // a 2xx on an empty body shouldn't happen, but treat as "on"
  } catch (err) {
    if (!(err instanceof BillingError)) return { on: false, code: 'init_network' };
    if (err.status === 400) return { on: true }; // 400 invalid_body ⇒ mode on
    return { on: false, code: `init_http_${err.status}` };
  }
}

export async function openBillingPortal(): Promise<{ url: string }> {
  return billingFetch<{ url: string }>('/portal', { method: 'POST' });
}

// ─── Scheduled downgrade (LEA-299) ───────────────────────────────────────
//
// Schedule a move to a LOWER plan that applies at the end of the current
// billing period. The user keeps their current (higher) plan until renewal;
// existing credits are kept and only the monthly allowance lowers from the
// next cycle. Upgrades are NOT done here — they apply immediately via
// startSubscriptionCheckout.
export async function scheduleDowngrade(
  tier: SubscriptionTier,
): Promise<{
  ok: boolean;
  pendingTier: SubscriptionTier;
  effectiveAt: number;
  alreadyScheduled?: boolean;
}> {
  return billingFetch('/subscription/downgrade', {
    method: 'POST',
    body: JSON.stringify({ tier }),
  });
}

// Cancel a pending downgrade before it takes effect — the subscription stays
// on its current plan.
export async function cancelDowngrade(): Promise<{ ok: boolean }> {
  return billingFetch('/subscription/downgrade/cancel', { method: 'POST' });
}

// ─── Inline cancellation + reactivation (LEA-333) ────────────────────────
//
// Schedule cancellation at the END of the current billing period
// (cancel_at_period_end) from our own UI. The Stripe portal stays for
// payment-method / invoice management — only the cancel action moves inline.
// `feedback` is OPTIONAL and persisted best-effort on the server (§312k: a
// feedback failure never blocks the cancel). Everything in it is skippable.

export type CancellationFeedback = {
  reasonCategory?: string | null;
  freeText?: string | null;
  qualityAudit?: {
    rightCompanies?: 'good' | 'bad' | null;
    rightContacts?: 'good' | 'bad' | null;
    contactDataUsable?: 'good' | 'bad' | null;
    enoughLeads?: 'good' | 'bad' | null;
    helpedWin?: 'good' | 'bad' | null;
  } | null;
  leadReference?: {
    leadId: string;
    profileId?: string | null;
    runId?: string | null;
    leadQueryId?: string | null;
  } | null;
  capabilityGap?: string | null;
  switchingToTool?: string | null;
  competitorAdvantage?: string | null;
  // LEA-555: muss mit ChurnSaveLever (@cortex/schemas) und dem zod-Enum der
  // Cancel-Route synchron bleiben.
  saveOfferedLever?:
    | 'downgrade'
    | 'pause'
    | 'help'
    | 'trial_reminder'
    | 'discount'
    | 'vip'
    | 'none'
    | null;
  saveAccepted?: boolean;
};

export type CancelResult = {
  ok?: boolean;
  accessUntilMs?: number | null;
  alreadyScheduled?: boolean;
  alreadyCanceled?: boolean;
  // LEA-554: die Sub war im Stripe-Trial und wurde SOFORT beendet (nicht zum
  // Periodenende). accessUntilMs ist dann "jetzt". Die UI muss daran den
  // Done-Text umschalten — "Zugang beendet" statt "Zugang bis {Datum}" — sonst
  // verspricht der Bestaetigungsschritt Zugang, den der Server Sekunden vorher
  // entzogen hat (auf Staging so gesehen, LEA-555).
  trialEndedImmediately?: boolean;
  // LEA-561: Join-Schluessel zum churnFeedback-Doc, damit der Save-Record den
  // Kuendigungsgrund mitfuehren kann.
  churnFeedbackId?: string | null;
};

export async function cancelSubscription(feedback?: CancellationFeedback): Promise<CancelResult> {
  return retryOnceOnTransient(() =>
    billingFetch<CancelResult>('/subscription/cancel', {
      method: 'POST',
      body: JSON.stringify(feedback ?? {}),
    }),
  );
}

// LEA-561: ein Record pro Durchlauf des Kuendigungs-Modals — der Zaehler zur
// Save-Rate, den es bisher nicht gab.
//
// Bewusst OHNE retryOnceOnTransient und mit verschlucktem Fehler: eine Messung
// darf niemals eine Kuendigung oder einen Save blockieren, und der Aufrufer
// feuert das teils aus einem Unmount-/pagehide-Pfad, in dem er auf nichts mehr
// warten kann. `keepalive` sorgt dafuer, dass der Request einen
// Tab-Wechsel/Close ueberlebt; die Payload ist klein genug fuer das 64-KB-Limit.
export type CancellationEventInput = {
  flowId: string;
  outcome: string;
  offeredLevers?: string[];
  acceptedLever?: string | null;
  lastStep?: string;
  reasonCategory?: string | null;
  churnFeedbackId?: string | null;
};

export async function recordCancellationEvent(input: CancellationEventInput): Promise<void> {
  try {
    await fetch(`${BILLING_BASE}/cancellation-event`, {
      method: 'POST',
      credentials: 'include',
      keepalive: true,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    });
  } catch {
    // Messung ist best-effort — niemals eskalieren.
  }
}

// LEA-557: der 30%-Retention-Coupon (3 Monate, Janik 04.09.) auf die laufende
// Sub. Idempotent — ein zweiter Klick liefert alreadyApplied statt Fehler.
export async function applyRetentionDiscount(): Promise<{
  ok: boolean;
  applied?: boolean;
  alreadyApplied?: boolean;
}> {
  return retryOnceOnTransient(() =>
    billingFetch('/subscription/retention-discount', { method: 'POST' }),
  );
}

// LEA-560: Win-back-Checkout — prefilled Session mit Retention-Coupon auf dem
// alten Customer. Der Server validiert die Berechtigung (trialCanceledAt,
// kein aktives Abo, Coupon noch nicht eingeloest).
export async function startWinbackCheckout(): Promise<{ url?: string; sessionId?: string }> {
  return retryOnceOnTransient(() => billingFetch('/checkout/winback', { method: 'POST' }));
}

// LEA-558: VIP-Einrichtung anfragen — Marker + Gruender-Ping, keine
// Kuendigung, kein Stripe-Call. Idempotent (erster Klick gewinnt).
export async function requestVipSetup(reasonCategory?: string | null): Promise<{
  ok: boolean;
  requested?: boolean;
  alreadyRequested?: boolean;
}> {
  return retryOnceOnTransient(() =>
    billingFetch('/subscription/vip-setup', {
      method: 'POST',
      body: JSON.stringify({ reasonCategory: reasonCategory ?? null }),
    }),
  );
}

// LEA-558: Lazy-Kontext fuer das VIP-Gating (leadsDelivered === 0). Nur beim
// Modal-Oeffnen geholt, nie im /state-Polling. null = unbekannt — ehrlicher
// als 0, das die Karte faelschlich freischalten wuerde.
export async function fetchChurnContext(): Promise<{ leadsDelivered: number | null }> {
  try {
    return await billingFetch('/churn-context', { method: 'GET' });
  } catch {
    return { leadsDelivered: null };
  }
}

// Undo a scheduled cancellation — the subscription stays active. Idempotent
// server-side (returns ok even if nothing was scheduled).
export async function reactivateSubscription(): Promise<{ ok: boolean }> {
  return retryOnceOnTransient(() => billingFetch('/subscription/cancel/undo', { method: 'POST' }));
}

// ─── Pause / resume (LEA-333, LEA-524) ────────────────────────────────────
//
// Pause freezes billing (Stripe pause_collection): no charges, no new monthly
// allowance, but access + remaining credits are retained.
//
// LEA-524: die Pause laeuft nach 1, 2 oder 3 Monaten verbindlich wieder an —
// kein unbefristetes Pausieren mehr. `months` weglassen heisst 3 (der Server
// faengt fehlenden/ungueltigen Body genauso ab, damit ein alter Client nach dem
// Deploy nicht bricht). Faellige Rechnungen des bereits begonnenen Zyklus
// werden serverseitig VOR dem Pausieren eingezogen; die Pause ueberspringt nur
// kuenftige Verlaengerungen.
export async function pauseSubscription(months?: 1 | 2 | 3): Promise<{
  ok: boolean;
  alreadyPaused?: boolean;
  pauseResumesAt?: number | null;
  months?: 1 | 2 | 3;
}> {
  return retryOnceOnTransient(() =>
    billingFetch('/subscription/pause', {
      method: 'POST',
      body: JSON.stringify({ months: months ?? 3 }),
    }),
  );
}

export async function resumeSubscription(): Promise<{ ok: boolean; notPaused?: boolean }> {
  return retryOnceOnTransient(() => billingFetch('/subscription/resume', { method: 'POST' }));
}

// ─── Read-only history endpoints (Account → Billing tab) ─────────────────

export async function getPurchases(): Promise<CreditPurchase[]> {
  const { purchases } = await billingFetch<{ purchases: CreditPurchase[] }>('/purchases');
  return purchases;
}

export async function getSubscription(): Promise<SubscriptionDoc | null> {
  const { subscription } = await billingFetch<{ subscription: SubscriptionDoc | null }>(
    '/subscription',
  );
  return subscription;
}

export async function getSubscriptionEvents(): Promise<SubscriptionEvent[]> {
  const { events } = await billingFetch<{ events: SubscriptionEvent[] }>(
    '/subscription/events',
  );
  return events;
}

export async function getCreditEvents(limit?: number): Promise<CreditEvent[]> {
  const qs = limit ? `?limit=${limit}` : '';
  const { events } = await billingFetch<{ events: CreditEvent[] }>(`/credit-events${qs}`);
  return events;
}

export async function getRefunds(): Promise<Refund[]> {
  const { refunds } = await billingFetch<{ refunds: Refund[] }>('/refunds');
  return refunds;
}

// ─── Helpers ─────────────────────────────────────────────────────────────

/**
 * Wait for the billing webhook to land after a Stripe checkout return.
 * Stripe redirects ~1-2s before the webhook fires; we poll /state up to
 * `attempts` times with `delayMs` gap between tries.
 *
 * Resolves with the first state where `predicate` returns true. If no
 * attempt satisfies the predicate, returns the last fetched state (so the
 * UI still has something to render — typically the pre-webhook snapshot).
 *
 * `predicate` also receives the 1-based attempt number, so a caller can
 * hold out for the DEFINITIVE signal early and accept a weaker fallback
 * only late in the budget (LEA-430 — see BillingReturnPage).
 *
 * Per-attempt errors are tolerated: a single 503/504 blip mid-poll must not
 * fail the whole wait and show "something went wrong" to someone who just
 * paid. We only reject if EVERY attempt failed (i.e. we never got a state).
 */
export async function pollBillingStateUntil(
  predicate: (state: BillingStatePublic, attempt: number) => boolean,
  opts: { attempts?: number; delayMs?: number } = {},
): Promise<BillingStatePublic> {
  const attempts = opts.attempts ?? 4;
  const delayMs = opts.delayMs ?? 1000;
  let last: BillingStatePublic | null = null;
  let lastErr: unknown = null;
  for (let i = 0; i < attempts; i++) {
    try {
      const state = await getBillingState();
      last = state;
      if (predicate(state, i + 1)) return state;
    } catch (err) {
      lastErr = err;
    }
    if (i < attempts - 1) {
      await new Promise((r) => setTimeout(r, delayMs));
    }
  }
  if (!last) throw lastErr ?? new Error('billing state unavailable');
  return last;
}
